import { useEffect, useReducer } from 'react';
import { EMPTY_TERMS, typedFields, type MissionTerms } from './missionTerms';

interface TermsState {
  draft: MissionTerms;
  /** What the last save wrote, until the refetch says the same: without it the card reads « modified » in between. */
  saved: MissionTerms | null;
  seededFor: string | null;
}

export type TermsAction =
  | { type: 'reset' }
  | { type: 'seed'; issueKey: string; server: MissionTerms }
  | { type: 'edit'; patch: Partial<MissionTerms> }
  | { type: 'saved'; snapshot: MissionTerms | null };

const INITIAL: TermsState = { draft: EMPTY_TERMS, saved: null, seededFor: null };

const termsReducer = (state: TermsState, action: TermsAction): TermsState => {
  switch (action.type) {
    case 'reset':
      return INITIAL;
    case 'seed':
      return state.seededFor === action.issueKey
        ? state
        : { ...state, seededFor: action.issueKey, draft: { ...action.server, ...typedFields(state.draft) } };
    case 'edit':
      return { ...state, draft: { ...state.draft, ...action.patch } };
    case 'saved':
      return { ...state, saved: action.snapshot };
  }
};

/** The terms card's draft and last saved snapshot: reset per mission, seeded once when the read settles (CA1). */
export const useMissionTerms = (issueKey: string | null, server: MissionTerms, loading: boolean) => {
  const [state, dispatch] = useReducer(termsReducer, INITIAL);
  useEffect(() => dispatch({ type: 'reset' }), [issueKey]);
  useEffect(() => {
    if (issueKey && !loading) dispatch({ type: 'seed', issueKey, server });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueKey, loading]);
  return { terms: state.draft, savedTerms: state.saved, dispatchTerms: dispatch };
};
